import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  HStack,
  VStack,
  Box,
  Flex,
  Text,
  Badge,
  Select,
  useToast,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import AdminModalContent from '../layout/AdminModalContent';
import {
  primaryButtonProps,
  secondaryButtonProps,
  dangerOutlineButtonProps,
  getStatusBadgeProps,
} from '../../theme/adminUi';
import SchedulerModalInfo from './SchedulerModalInfo';
import SchedulerModalForm from './SchedulerModalForm';
import CancelModal from './CancelModal';
import SchedulerReceptionModalForm from './SchedulerReceptionModalForm';
import ServiceAlertsModal from './ServiceAlertsModal';
import ConfirmModal from '../modal/ConfirmModal';
import { deleteData, getData, postData } from 'utils/httpClient';
import { FaPrint } from 'react-icons/fa';
import authService from 'services/authService';
import { useServiceProductivityTimer } from '../../hooks/useServiceProductivityTimer';
import { FINISHED_STATUSES } from '../../utils/serviceProductivityTimer';
import { hasPendingAlerts } from '../../utils/vehicleAlerts';
import { validateStockForProducts } from '../../utils/validateServiceStock';
import { isSimplifiedWorkshopView } from '../../utils/warrantyTypes';

const SchedulerModal = ({
  isModalOpen,
  handleCloseModal,
  selectedEvent,
  setSelectedEvent,
  handleUpdateEvent,
  vehicleService,
  putVehicleService,
  putDynamicTest,
  putVehicleData,
  productsList,
  statusList,
  mechanicsList,
  onAlertsChanged,
  onAppointmentsRefresh,
  onVehicleServiceUpdated,
  onAppointmentLocalUpdate,
  appointmentList = [],
  holidayList = [],
}) => {
  const toast = useToast();
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [selectedRecommendedProducts, setSelectedRecommendedProducts] = useState([]);
  const [completedEvents, setCompletedEvents] = useState({});
  const [mechanicObservations, setMechanicObservations] = useState('');
  const [customerObservations, setCustomerObservations] = useState('');
  const [serviceObservations, setServiceObservations] = useState('');
  const [volkswagenCost, setVolkswagenCost] = useState('');
  const [assistant, setAssistant] = useState(0);
  const [dynamicTestStart, setDynamicTestStart] = useState(null);
  const [dynamicTestEnd, setDynamicTestEnd] = useState(null);
  const [dynamicTestNotApplicable, setDynamicTestNotApplicable] = useState(false);
  const [isSavingDynamicTest, setIsSavingDynamicTest] = useState(false);
  const [cancelObservation, setCancelObservation] = useState(null);
  const [cancelModalState, setCancelModalState] = useState({ isOpen: false });
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isSavingAndClosing, setIsSavingAndClosing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeletingService, setIsDeletingService] = useState(false);
  const [scheduleOverlayOpen, setScheduleOverlayOpen] = useState(false);
  const [stockAlertState, setStockAlertState] = useState({
    isOpen: false,
    shortages: [],
  });
  const [hasAlerts, setHasAlerts] = useState(false);
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [mechanicStatusDraft, setMechanicStatusDraft] = useState('');
  const [associatingLastReception, setAssociatingLastReception] = useState(false);
  const {
    isOpen: isAlertsOpen,
    onOpen: onAlertsOpen,
    onClose: onAlertsClose,
  } = useDisclosure();

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const currentStatus = selectedEvent?.appointment?.status;
  const isUserMechanic = authService.isMechanic();
  // Pendiente/Confirmado/Recepcionado: asocia datos y deja el servicio en Recepcionado.
  const showAssociateLastReception =
    !isUserMechanic &&
    ['Pendiente', 'Confirmado', 'Recepcionado'].includes(currentStatus);

  const appointmentWithStartingMechanic = (appointment, nextStatus) => {
    const nextAppointment = { ...appointment, status: nextStatus };
    if (!isUserMechanic || nextStatus !== 'En curso' || appointment?.status === 'En curso') {
      return nextAppointment;
    }
    const currentUser = authService.getCurrentUser();
    const mechanicId = Number(currentUser?.id);
    if (!Number.isFinite(mechanicId) || mechanicId <= 0) return nextAppointment;
    const mechanic =
      mechanicsList.find((item) => Number(item.id) === mechanicId) || {
        id: mechanicId,
        name: currentUser?.name,
      };
    return {
      ...nextAppointment,
      mechanicId,
      mechanic,
    };
  };

  useEffect(() => {
    setMechanicStatusDraft(
      currentStatus === 'Completado' ? 'Finalizado' : currentStatus || '',
    );
  }, [currentStatus, selectedEvent?.appointment?.id]);

  const hasAssistantAssigned =
    Number(assistant) > 0 ||
    Number(vehicleService?.assistantId) > 0;

  const productivityTimer = useServiceProductivityTimer(
    vehicleService,
    currentStatus,
    { role: 'mechanic', enabled: true },
  );
  const assistantProductivityTimer = useServiceProductivityTimer(
    vehicleService,
    currentStatus,
    { role: 'assistant', enabled: hasAssistantAssigned },
  );

  // Si asignan ayudante a mitad del servicio en curso, arrancar su reloj
  useEffect(() => {
    if (
      currentStatus === 'En curso' &&
      hasAssistantAssigned &&
      assistantProductivityTimer.enabled
    ) {
      assistantProductivityTimer.autoStartIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAssistantAssigned, currentStatus]);

  const checkAlerts = useCallback(async (vehicleId) => {
    if (!vehicleId) {
      setHasAlerts(false);
      return;
    }
    try {
      const response = await getData(`/vehicleAlerts/vehicle/${vehicleId}`, {
        skipLoader: true,
      });
      const alerts = response?.payload?.alerts || [];
      setHasAlerts(hasPendingAlerts(alerts));
    } catch {
      setHasAlerts(false);
    }
  }, []);

  useEffect(() => {
    if (!vehicleService) {
      setSelectedRecommendedProducts([]);
      setAdditionalProducts([]);
      setCompletedEvents({});
      return;
    }

    const recommendedProducts = vehicleService.service?.recommendedProducts
      ?.map((product) => ({
        ...product,
        isSelected: vehicleService.usedProducts?.some(
          (usedProduct) => usedProduct.productId === product.productId,
        ),
      }))
      .flat();

    const selectedProducts = recommendedProducts
      ?.filter((product) => product.isSelected)
      .map((product) => ({
        id: product.productId,
        name: product.product?.name,
      }));

    setSelectedRecommendedProducts(selectedProducts || []);

    const recommendedIds = new Set(
      (vehicleService.service?.recommendedProducts || []).map((p) => p.productId),
    );
    const additional = (vehicleService.usedProducts || [])
      .filter((up) => !recommendedIds.has(up.productId))
      .map((up) => ({
        id: up.productId,
        usedProductId: up.id,
        name: up.productName || up.product?.name || '',
        code: up.product?.code || '',
        quantity: Number(up.quantity) || 1,
        price: up.price,
        currencyId: up.currencyId,
      }));
    setAdditionalProducts(additional);

      const eventCompletionState = {};
      vehicleService.vehicleServiceEventsHistories?.forEach((history) => {
        const comment = history.comment;
        let cleanComment = '';
        if (
          comment &&
          comment !== 0 &&
          String(comment).trim() !== '0' &&
          String(comment).trim() !== ''
        ) {
          cleanComment = String(comment);
        }
        const done =
          history.completed ||
          history.status === 'Ok' ||
          false;
        let status = null;
        if (history.status === 'Ok' || history.status === 'NoOk') {
          status = history.status;
        } else if (done) {
          status = 'Ok';
        }
        eventCompletionState[history.id] = {
          completed: done,
          comment: cleanComment,
          status,
          reparado: !!history.reparado,
        };
      });
      setCompletedEvents(eventCompletionState);

      setMechanicObservations(vehicleService.mechanicObservation || '');
      setCustomerObservations(vehicleService.customerObservations || '');
      setServiceObservations(vehicleService.observations || '');
      setVolkswagenCost(
        vehicleService.volkswagenCost != null && vehicleService.volkswagenCost !== ''
          ? String(vehicleService.volkswagenCost)
          : vehicleService.service?.volkswagenCost != null
            ? String(vehicleService.service.volkswagenCost)
            : '',
      );
      setAssistant(
        vehicleService.assistantId ? String(vehicleService.assistantId) : '0',
      );
      setDynamicTestStart(vehicleService.dynamicTestStart || null);
      setDynamicTestEnd(vehicleService.dynamicTestEnd || null);
      setDynamicTestNotApplicable(Boolean(vehicleService.dynamicTestNotApplicable));

      // Mantener el modal alineado con el servicio (estado, tipo, km, garantía).
      if (selectedEvent?.appointment) {
        setSelectedEvent((prev) => {
          if (!prev?.appointment) return prev;
          const nextStatus =
            vehicleService.status?.name || prev.appointment.status;
          return {
            ...prev,
            appointment: {
              ...prev.appointment,
              status: nextStatus,
              serviceTypeId:
                vehicleService.serviceTypeId ?? prev.appointment.serviceTypeId,
              serviceType:
                vehicleService.serviceType || prev.appointment.serviceType,
              warrantyType:
                vehicleService.warrantyType ?? prev.appointment.warrantyType,
              vehicleService: {
                ...(prev.appointment.vehicleService || {}),
                id: vehicleService.id,
                warrantyType: vehicleService.warrantyType,
              },
              vehicle: {
                ...prev.appointment.vehicle,
                ...(vehicleService.vehicle || {}),
                ...(vehicleService.odometer != null
                  ? { kilometers: vehicleService.odometer }
                  : {}),
              },
            },
          };
        });
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleService]);

  useEffect(() => {
    checkAlerts(selectedEvent?.appointment?.vehicle?.id);
  }, [selectedEvent?.appointment?.vehicle?.id, isAlertsOpen, checkAlerts]);

  useEffect(() => {
    if (!isModalOpen) {
      setIsCloseConfirmOpen(false);
      setIsSavingAndClosing(false);
    }
  }, [isModalOpen]);

  useEffect(() => {
    const loadServices = async () => {
      const typeId = selectedEvent?.appointment?.serviceTypeId;
      if (!typeId) return;
      try {
        const res = await getData(`/services?serviceType=${typeId}`, {
          skipLoader: true,
        });
        setServicesCatalog(res?.payload?.services || []);
      } catch {
        setServicesCatalog([]);
      }
    };
    if (isModalOpen) loadServices();
  }, [isModalOpen, selectedEvent?.appointment?.serviceTypeId]);

  const openPdfByDocumentId = async (documentId) => {
    if (!documentId) return;
    try {
      const response = await getData('/document/' + documentId);
      const document = response.payload.document;
      if (document?.file) {
        const pdfData = new Uint8Array(document.file.data);
        const blob = new Blob([pdfData], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch {
      toast({
        title: 'PDF no disponible',
        description: 'Guarde el servicio nuevamente o contacte soporte.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const buildProductsForStockCheck = (targetStatus) => {
    const recommended = vehicleService?.service?.recommendedProducts || [];

    if (targetStatus === 'Confirmado') {
      return recommended
        .filter((rp) => rp.productId)
        .map((rp) => ({
          productId: rp.productId,
          productName: rp.product?.name,
          quantity: Number(rp.quantity) || 1,
        }));
    }

    return [
      ...recommended
        .filter((product) =>
          selectedRecommendedProducts.some((p) => p.id === product.productId),
        )
        .map((product) => ({
          productId: product.productId,
          productName: product.product?.name,
          quantity: product.quantity,
        })),
      ...additionalProducts.map((product) => ({
        productId: product.id,
        productName: product.name,
        quantity: product.quantity,
      })),
    ];
  };

  const showStockShortageAlert = (stockCheck) => {
    setStockAlertState({
      isOpen: true,
      shortages: stockCheck.shortages || [],
    });
    toast({
      title: 'Falta de stock',
      description: stockCheck.message,
      status: 'warning',
      duration: 8000,
      isClosable: true,
    });
  };

  const runStockCheck = async (targetStatus) => {
    const products = buildProductsForStockCheck(targetStatus);
    if (!products.length) {
      return { ok: true };
    }
    return validateStockForProducts(products, vehicleService?.id);
  };

  const handleSaveVehicleService = async (status, options = {}) => {
    const recommended = vehicleService?.service?.recommendedProducts || [];
    const currentUser = authService.getCurrentUser();
    const timerPayload = productivityTimer.getSavePayload();
    const assistantTimerPayload = assistantProductivityTimer.getSavePayload();
    const isTerminalStatus = FINISHED_STATUSES.includes(status);

    const nextDynamicStart =
      options.dynamicTestStart !== undefined
        ? options.dynamicTestStart
        : dynamicTestStart ?? vehicleService?.dynamicTestStart ?? null;
    const nextDynamicEnd =
      options.dynamicTestEnd !== undefined
        ? options.dynamicTestEnd
        : dynamicTestEnd ?? vehicleService?.dynamicTestEnd ?? null;
    const nextDynamicNotApplicable =
      options.dynamicTestNotApplicable !== undefined
        ? options.dynamicTestNotApplicable
        : dynamicTestNotApplicable ??
          Boolean(vehicleService?.dynamicTestNotApplicable);

    const previousStatus =
      selectedEvent?.appointment?.status || vehicleService?.status?.name;
    const isStartingService =
      status === 'En curso' &&
      (previousStatus === 'Recepcionado' || !vehicleService?.serviceStart);
    const hasAssistant =
      Number(assistant) > 0 || Number(vehicleService?.assistantId) > 0;
    const isStartingAssistant =
      hasAssistant &&
      status === 'En curso' &&
      (isStartingService || !vehicleService?.assistantStart);

    const isSimplifiedView = isSimplifiedWorkshopView(
      selectedEvent?.appointment?.serviceType || vehicleService?.serviceType,
      [],
      {
        warrantyType:
          vehicleService?.warrantyType ||
          selectedEvent?.appointment?.warrantyType,
        description:
          vehicleService?.description ||
          selectedEvent?.appointment?.description,
        serviceName: vehicleService?.serviceName,
        agenda: selectedEvent?.appointment,
        service:
          vehicleService?.service || selectedEvent?.appointment?.service,
      },
    );

    const existingUsedByProductId = new Map(
      (vehicleService?.usedProducts || []).map((up) => [
        Number(up.productId),
        up,
      ]),
    );

    const nextUsedProducts = isSimplifiedView
      ? undefined
      : [
          ...recommended
            .filter((product) =>
              selectedRecommendedProducts.some(
                (p) => p.id === product.productId,
              ),
            )
            .map((product) => {
              const existing = existingUsedByProductId.get(
                Number(product.productId),
              );
              return {
                id: existing?.id,
                productId: product.productId,
                productName: product.product?.name,
                quantity: product.quantity,
                price: product.product?.currentCost,
                currencyId: product.product?.currencyId,
              };
            }),
          ...additionalProducts.map((product) => {
            const existing = existingUsedByProductId.get(Number(product.id));
            return {
              id: existing?.id,
              productId: product.id,
              productName: product.name,
              quantity: product.quantity,
              price: product.price,
              currencyId: product.currencyId,
            };
          }),
        ];

    const nextEventHistories = isSimplifiedView
      ? undefined
      : vehicleService?.vehicleServiceEventsHistories?.map((eventHistory) => ({
          ...eventHistory,
          comment: completedEvents[eventHistory.id]?.comment ?? null,
          status: completedEvents[eventHistory.id]?.status || null,
          reparado: !!completedEvents[eventHistory.id]?.reparado,
          completed:
            completedEvents[eventHistory.id]?.status === 'Ok' ||
            completedEvents[eventHistory.id]?.completed ||
            false,
        }));

    const updatedVehicleService = {
      ...vehicleService,
      creatorUserId: vehicleService?.creatorUserId || currentUser?.id || null,
      date: selectedEvent?.appointment?.date || vehicleService?.date,
      time: selectedEvent?.appointment?.time || vehicleService?.time,
      serviceStart: isStartingService
        ? new Date()
        : vehicleService?.serviceStart || timerPayload.serviceStart,
      serviceEnd: isTerminalStatus
        ? timerPayload.serviceEnd || new Date()
        : null,
      totalTime: timerPayload.totalTime ?? vehicleService?.totalTime,
      assistantStart: hasAssistant
        ? isStartingAssistant
          ? new Date()
          : vehicleService?.assistantStart ||
            assistantTimerPayload.assistantStart
        : vehicleService?.assistantStart || null,
      assistantEnd:
        hasAssistant && isTerminalStatus
          ? assistantTimerPayload.assistantEnd || new Date()
          : hasAssistant
            ? null
            : vehicleService?.assistantEnd || null,
      assistantTotalTime: hasAssistant
        ? assistantTimerPayload.assistantTotalTime ??
          vehicleService?.assistantTotalTime
        : vehicleService?.assistantTotalTime ?? null,
      dynamicTestStart: nextDynamicStart,
      dynamicTestEnd: nextDynamicEnd,
      dynamicTestNotApplicable: nextDynamicNotApplicable,
      ...(nextUsedProducts !== undefined
        ? { usedProducts: nextUsedProducts }
        : {}),
      ...(nextEventHistories !== undefined
        ? { vehicleServiceEventsHistories: nextEventHistories }
        : {}),
      customerObservations: customerObservations,
      observations: serviceObservations,
      mechanicObservation: mechanicObservations,
      volkswagenCost: (() => {
        if (volkswagenCost === '' || volkswagenCost == null) return null;
        const parsed = Number(String(volkswagenCost).replace(',', '.'));
        return Number.isNaN(parsed) ? null : parsed;
      })(),
      statusId:
        statusList.find((statusItem) => statusItem.name === status)?.id || null,
      canceledObservation: cancelObservation,
      assistantId: Number(assistant) === 0 ? null : Number(assistant),
      mechanicId: (() => {
        const currentUserId = Number(currentUser?.id);
        if (
          isUserMechanic &&
          status === 'En curso' &&
          previousStatus !== 'En curso' &&
          Number.isFinite(currentUserId) &&
          currentUserId > 0
        ) {
          return currentUserId;
        }
        return (
          Number(selectedEvent?.appointment?.mechanicId) ||
          Number(vehicleService?.mechanicId) ||
          null
        );
      })(),
      customerTransferId: (() => {
        const raw =
          selectedEvent?.appointment?.customerTransferId ??
          vehicleService?.customerTransferId;
        const id = Number(raw);
        return Number.isFinite(id) && id > 0 ? id : null;
      })(),
    };

    const productsForStockCheck = buildProductsForStockCheck(status);
    if (productsForStockCheck.length) {
      const stockCheck = await validateStockForProducts(
        productsForStockCheck,
        vehicleService?.id,
      );
      if (!stockCheck.ok) {
        if (status === 'Confirmado') {
          showStockShortageAlert(stockCheck);
        } else if (!options.silent) {
          toast({
            title: 'Stock insuficiente',
            description: stockCheck.message,
            status: 'error',
            duration: 6000,
            isClosable: true,
          });
        }
        throw Object.assign(new Error(stockCheck.message), { isStockError: true });
      }
    }

    let response;
    try {
      // Agenda primero: el PDF de recepción toma el traslado desde ahí
      if (selectedEvent?.appointment) {
        await handleUpdateEvent({
          ...selectedEvent.appointment,
          status,
          mechanicId:
            updatedVehicleService.mechanicId ??
            selectedEvent.appointment.mechanicId,
          mechanic:
            mechanicsList.find(
              (m) => Number(m.id) === Number(updatedVehicleService.mechanicId),
            ) || selectedEvent.appointment.mechanic,
          customerTransferId:
            updatedVehicleService.customerTransferId ??
            selectedEvent.appointment.customerTransferId,
        });
      }
      response = await putVehicleService(updatedVehicleService);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Error al guardar el servicio';
      if (!options.silent) {
        toast({
          title: 'No se pudo guardar',
          description: msg,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
      throw new Error(msg);
    }

    if (!response) {
      const msg = 'No se pudo guardar el servicio.';
      if (!options.silent) {
        toast({
          title: 'No se pudo guardar',
          description: msg,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
      throw new Error(msg);
    }
    if (isTerminalStatus) {
      productivityTimer.resetAfterSave();
      assistantProductivityTimer.resetAfterSave();
    }
    if (!options.silent) {
      toast({
        title: 'Servicio actualizado',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    }
    return response;
  };

  const handleSaveVehicleData = async (vehicle) => {
    await putVehicleData(vehicle);
  };

  const handleAssociateLastReception = async () => {
    const serviceId = vehicleService?.id;
    if (!serviceId || !selectedEvent?.appointment) {
      toast({
        title: 'Servicio no disponible',
        description: 'No se encontró el servicio agendado.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    const statusNow = selectedEvent.appointment.status;
    if (!['Pendiente', 'Confirmado', 'Recepcionado'].includes(statusNow)) {
      toast({
        title: 'Estado no permitido',
        description:
          'Solo se puede asociar recepción en Pendiente, Confirmado o Recepcionado.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Conservar la petición del cliente tal como está en pantalla (no pisarla).
    const preservedPetition = customerObservations;

    setAssociatingLastReception(true);
    try {
      const response = await postData(
        `/vehicleServices/${serviceId}/associate-last-reception`,
        {},
        { skipLoader: true },
      );
      const updated = response?.payload?.vehicleService;
      if (!updated) {
        throw new Error('No se obtuvo el servicio actualizado.');
      }

      // Forzar conservación de la petición del cliente en el objeto recibido.
      updated.customerObservations =
        preservedPetition ??
        vehicleService?.customerObservations ??
        updated.customerObservations ??
        null;

      const nextStatus = updated.status?.name || 'Recepcionado';
      const nextAppointment = {
        ...selectedEvent.appointment,
        // No mover fecha/hora del turno en calendario.
        status: nextStatus,
        serviceTypeId:
          updated.serviceTypeId ?? selectedEvent.appointment.serviceTypeId,
        serviceType:
          updated.serviceType || selectedEvent.appointment.serviceType,
        warrantyType:
          updated.warrantyType ?? selectedEvent.appointment.warrantyType,
        vehicleService: {
          ...(selectedEvent.appointment.vehicleService || {}),
          id: updated.id,
          warrantyType: updated.warrantyType,
        },
        vehicle: {
          ...selectedEvent.appointment.vehicle,
          ...(updated.vehicle || {}),
          ...(updated.odometer != null
            ? { kilometers: updated.odometer }
            : {}),
        },
      };

      // 1) Actualizar servicio en el padre → useEffect del modal refresca campos.
      onVehicleServiceUpdated?.(updated);

      // 2) Actualizar evento/modal al instante (vista Recepcionado).
      setSelectedEvent((prev) =>
        prev
          ? {
              ...prev,
              appointment: nextAppointment,
            }
          : prev,
      );

      // 3) Actualizar listado del calendario en memoria (sin PUT a agenda:
      //    el backend ya sincronizó el estado; un PUT extra pisaba datos).
      onAppointmentLocalUpdate?.(nextAppointment);

      // 4) Restaurar petición del cliente tras el sync del useEffect.
      setCustomerObservations(preservedPetition || '');

      toast({
        title: 'Recepción asociada',
        description:
          'Se actualizó el servicio a Recepcionado con los datos de la última recepción (sin cambiar la petición del cliente).',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'No se pudo asociar la recepción',
        description:
          err?.message ||
          'No hay una recepción previa para este vehículo.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setAssociatingLastReception(false);
    }
  };

  const handleSaveRecivedVehicleData = async () => {
    await handleSaveVehicleData(selectedEvent.appointment.vehicle);
    const result = await handleSaveVehicleService('Recepcionado', { silent: true });
    toast({
      title: 'Recepción guardada',
      description: 'Datos actualizados correctamente.',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
    const docId =
      result?.documentId ||
      result?.payload?.vehicleService?.documentId;
    if (docId) await openPdfByDocumentId(docId);
  };

  const handlePrintReception = async () => {
    const serviceId = vehicleService?.id;
    if (!serviceId) {
      toast({
        title: 'Documento no disponible',
        description: 'No se encontró el servicio para imprimir.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }
    try {
      const response = await postData(
        `/vehicleServices/${serviceId}/reception-pdf`,
        {},
      );
      const docId =
        response?.payload?.documentId ||
        response?.payload?.vehicleService?.documentId;
      if (!docId) {
        throw new Error('No se obtuvo el documento generado.');
      }
      await openPdfByDocumentId(docId);
    } catch (err) {
      toast({
        title: 'No se pudo imprimir',
        description:
          err?.response?.data?.message ||
          err?.message ||
          'Intente guardar la recepción e imprimir nuevamente.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    }
  };

  const getNextStatus = (status) => {
    switch (status) {
      case 'Pendiente':
        return 'Confirmado';
      case 'Confirmado':
        return 'Recepcionado';
      case 'Recepcionado':
        return 'En curso';
      case 'En curso':
        return 'Completado';
      case 'Completado':
        return 'Finalizado';
      default:
        return 'Pendiente';
    }
  };

  const getButtonText = (status) => {
    switch (status) {
      case 'Pendiente':
        return 'Confirmar';
      case 'Confirmado':
        return 'Recepción';
      case 'Recepcionado':
        return 'Iniciar';
      case 'En curso':
        return 'Finalizar servicio';
      case 'Completado':
        return 'Finalizar';
      default:
        return 'Actualizar';
    }
  };

  const handleStartDynamicTest = async () => {
    if (dynamicTestStart || dynamicTestNotApplicable || !vehicleService?.id || !putDynamicTest) {
      return;
    }
    const now = new Date().toISOString();
    setIsSavingDynamicTest(true);
    try {
      setDynamicTestStart(now);
      setDynamicTestNotApplicable(false);
      const saved = await putDynamicTest(vehicleService.id, {
        dynamicTestStart: now,
        dynamicTestEnd: null,
        dynamicTestNotApplicable: false,
      });
      setDynamicTestStart(saved?.dynamicTestStart || now);
      setDynamicTestEnd(saved?.dynamicTestEnd || null);
      setDynamicTestNotApplicable(Boolean(saved?.dynamicTestNotApplicable));
      toast({
        title: 'Prueba dinámica iniciada',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      setDynamicTestStart(null);
      toast({
        title: 'No se pudo iniciar la prueba',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingDynamicTest(false);
    }
  };

  const handleDynamicTestNotApplicableChange = async (checked) => {
    if (!vehicleService?.id || !putDynamicTest) return;
    if (dynamicTestStart && !dynamicTestEnd && checked) return;

    setIsSavingDynamicTest(true);
    try {
      setDynamicTestNotApplicable(checked);
      if (checked) {
        setDynamicTestStart(null);
        setDynamicTestEnd(null);
      }
      const saved = await putDynamicTest(vehicleService.id, {
        dynamicTestNotApplicable: checked,
        ...(checked
          ? { dynamicTestStart: null, dynamicTestEnd: null }
          : {}),
      });
      setDynamicTestNotApplicable(Boolean(saved?.dynamicTestNotApplicable));
      setDynamicTestStart(saved?.dynamicTestStart || null);
      setDynamicTestEnd(saved?.dynamicTestEnd || null);
      toast({
        title: checked
          ? 'Prueba dinámica marcada como no aplica'
          : 'Prueba dinámica requerida nuevamente',
        status: 'info',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      setDynamicTestNotApplicable(Boolean(vehicleService?.dynamicTestNotApplicable));
      setDynamicTestStart(vehicleService?.dynamicTestStart || null);
      setDynamicTestEnd(vehicleService?.dynamicTestEnd || null);
      toast({
        title: 'No se pudo actualizar la prueba dinámica',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingDynamicTest(false);
    }
  };

  const handleEndDynamicTest = async () => {
    if (!dynamicTestStart || dynamicTestEnd || !vehicleService?.id || !putDynamicTest) {
      return;
    }
    const now = new Date().toISOString();
    setIsSavingDynamicTest(true);
    try {
      setDynamicTestEnd(now);
      const saved = await putDynamicTest(vehicleService.id, {
        dynamicTestStart,
        dynamicTestEnd: now,
      });
      setDynamicTestStart(saved?.dynamicTestStart || dynamicTestStart);
      setDynamicTestEnd(saved?.dynamicTestEnd || now);
      toast({
        title: 'Prueba dinámica finalizada',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      setDynamicTestEnd(null);
      toast({
        title: 'No se pudo finalizar la prueba',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingDynamicTest(false);
    }
  };

  const handleStatusUpdate = async () => {
    const nextStatus = getNextStatus(currentStatus);

    if (
      currentStatus === 'En curso' &&
      FINISHED_STATUSES.includes(nextStatus)
    ) {
      if (
        !dynamicTestNotApplicable &&
        (!dynamicTestStart || !dynamicTestEnd)
      ) {
        toast({
          title: 'Prueba dinámica incompleta',
          description:
            'Debe iniciar y finalizar la prueba dinámica, o marcar "No aplica prueba dinámica", antes de finalizar el servicio.',
          status: 'warning',
          duration: 4500,
          isClosable: true,
        });
        return;
      }
    }

    const updatedEvent = {
      ...selectedEvent,
      appointment: appointmentWithStartingMechanic(
        selectedEvent.appointment,
        nextStatus,
      ),
    };

    setIsUpdatingStatus(true);
    try {
      if (nextStatus === 'Confirmado') {
        const stockCheck = await runStockCheck('Confirmado');
        if (!stockCheck.ok) {
          showStockShortageAlert(stockCheck);
          return;
        }
      }

      if (nextStatus === 'En curso') {
        await handleSaveVehicleData(selectedEvent.appointment.vehicle);
        productivityTimer.autoStartIfNeeded();
        if (hasAssistantAssigned) {
          assistantProductivityTimer.autoStartIfNeeded();
        }
      }

      if (FINISHED_STATUSES.includes(nextStatus)) {
        productivityTimer.autoFinalizeIfNeeded();
        if (hasAssistantAssigned) {
          assistantProductivityTimer.autoFinalizeIfNeeded();
        }
      }

      const result = await handleSaveVehicleService(nextStatus, {
        silent: true,
        dynamicTestStart:
          dynamicTestStart ?? vehicleService?.dynamicTestStart ?? null,
        dynamicTestEnd:
          dynamicTestEnd ?? vehicleService?.dynamicTestEnd ?? null,
        dynamicTestNotApplicable:
          dynamicTestNotApplicable ??
          Boolean(vehicleService?.dynamicTestNotApplicable),
      });
      await handleUpdateEvent(updatedEvent.appointment);
      setSelectedEvent(updatedEvent);
      await onAppointmentsRefresh?.();

      if (nextStatus === 'Confirmado') {
        handleCloseModal();
      }

      if (nextStatus === 'Finalizado' || nextStatus === 'Completado') {
        const docId =
          result?.documentId || result?.payload?.vehicleService?.documentId;
        if (docId) await openPdfByDocumentId(docId);
      }

      toast({
        title: `Estado: ${nextStatus}`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      const isStockError = Boolean(err?.isStockError);
      toast({
        title: isStockError
          ? 'Falta de stock'
          : 'No se pudo actualizar el estado',
        description:
          err?.message || 'Revise los datos del servicio e intente nuevamente.',
        status: isStockError ? 'warning' : 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleMechanicChange = (mechanicId, mechanic) => {
    setSelectedEvent((prev) => {
      if (!prev?.appointment) return prev;
      return {
        ...prev,
        appointment: {
          ...prev.appointment,
          mechanicId,
          mechanic,
        },
      };
    });
  };

  const handleCancelService = async () => {
    try {
      productivityTimer.autoFinalizeIfNeeded();
      if (hasAssistantAssigned) {
        assistantProductivityTimer.autoFinalizeIfNeeded();
      }
      const updatedEvent = {
        ...selectedEvent,
        appointment: { ...selectedEvent.appointment, status: 'Cancelado' },
      };
      setSelectedEvent(updatedEvent);
      await handleUpdateEvent(updatedEvent.appointment);
      await handleSaveVehicleService('Cancelado', { silent: true });
      await onAppointmentsRefresh?.();
      toast({
        title: 'Servicio cancelado',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'No se pudo cancelar',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteService = async () => {
    const serviceId = Number(vehicleService?.id);
    if (!serviceId) {
      toast({
        title: 'No se pudo eliminar',
        description: 'No se encontró el servicio asociado a la cita.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsDeletingService(true);
    try {
      await deleteData(`/vehicleServices/${serviceId}`);
      setIsDeleteConfirmOpen(false);
      await onAppointmentsRefresh?.();
      handleCloseModal();
      toast({
        title: 'Servicio eliminado',
        description: 'La cita fue eliminada y el stock quedó restaurado.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'No se pudo eliminar',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsDeletingService(false);
    }
  };

  const applyMechanicStatus = async (nextStatus) => {
    if (!nextStatus || nextStatus === currentStatus || !selectedEvent?.appointment) return;
    const updatedEvent = {
      ...selectedEvent,
      appointment: appointmentWithStartingMechanic(
        selectedEvent.appointment,
        nextStatus,
      ),
    };
    setIsUpdatingStatus(true);
    try {
      await handleSaveVehicleService(nextStatus, { silent: true });
      await handleUpdateEvent(updatedEvent.appointment);
      setSelectedEvent(updatedEvent);
      await onAppointmentsRefresh?.();
      toast({
        title: `Estado: ${nextStatus}`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'No se pudo cambiar el estado',
        description: err?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const renderFooterButtons = () => {
    if (isUserMechanic) {
      if (currentStatus === 'Recepcionado') {
        return (
          <HStack spacing={2} width="100%" justify="flex-end" flexWrap="wrap">
            <Button
              {...secondaryButtonProps}
              leftIcon={<FaPrint />}
              onClick={handlePrintReception}
              isDisabled={isUpdatingStatus}
            >
              Imprimir recepción
            </Button>
            <Button
              {...primaryButtonProps}
              onClick={handleStatusUpdate}
              isLoading={isUpdatingStatus}
              loadingText="Iniciando..."
            >
              Iniciar servicio
            </Button>
          </HStack>
        );
      }
      if (currentStatus === 'En curso') {
        return (
          <HStack spacing={2} width="100%" justify="flex-end" flexWrap="wrap">
            <Button
              {...secondaryButtonProps}
              onClick={() => handleSaveVehicleService('En curso')}
              isDisabled={isUpdatingStatus}
            >
              Guardar
            </Button>
            <Button
              {...primaryButtonProps}
              onClick={handleStatusUpdate}
              isLoading={isUpdatingStatus}
              loadingText="Finalizando..."
            >
              {getButtonText(currentStatus)}
            </Button>
          </HStack>
        );
      }
      if (currentStatus === 'Completado' || currentStatus === 'Finalizado') {
        const statusOptions = currentStatus === 'Completado'
          ? ['Finalizado']
          : (statusList || [])
              .map((item) => item.name)
              .filter((name) => name && name !== 'Finalizado');
        return (
          <HStack spacing={2} width="100%" justify="flex-end" flexWrap="wrap">
            <Select
              size="sm"
              maxW="220px"
              bg="white"
              value={mechanicStatusDraft || currentStatus}
              onChange={(e) => setMechanicStatusDraft(e.target.value)}
              isDisabled={isUpdatingStatus}
            >
              {statusOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
            <Button
              {...primaryButtonProps}
              onClick={() => applyMechanicStatus(mechanicStatusDraft)}
              isLoading={isUpdatingStatus}
              isDisabled={!mechanicStatusDraft || mechanicStatusDraft === currentStatus}
              loadingText="Actualizando..."
            >
              Cambiar estado
            </Button>
          </HStack>
        );
      }
      return null;
    }

    if (currentStatus === 'Cancelado') {
      if (!authService.isAdmin()) return null;
      return (
        <Button
          {...dangerOutlineButtonProps}
          onClick={() => setIsDeleteConfirmOpen(true)}
          isDisabled={isDeletingService}
        >
          Eliminar servicio
        </Button>
      );
    }

    if (currentStatus === 'Finalizado') {
      return null;
    }

    if (currentStatus === 'Recepcionado') {
      return (
        <HStack spacing={2} width="100%" justify="flex-end" flexWrap="wrap">
          <Button
            {...secondaryButtonProps}
            leftIcon={<FaPrint />}
            onClick={handlePrintReception}
            isDisabled={isUpdatingStatus}
          >
            Imprimir recepción
          </Button>
          <Button
            {...secondaryButtonProps}
            onClick={handleSaveRecivedVehicleData}
            isDisabled={isUpdatingStatus}
          >
            Guardar recepción
          </Button>
          <Button
            {...primaryButtonProps}
            onClick={handleStatusUpdate}
            isLoading={isUpdatingStatus}
            loadingText="Iniciando..."
          >
            Iniciar servicio
          </Button>
        </HStack>
      );
    }

    const isInProgress = currentStatus === 'En curso';

    return (
      <HStack spacing={2} width="100%" justify="flex-end" flexWrap="wrap">
        <Button
          {...secondaryButtonProps}
          onClick={() => handleSaveVehicleService(currentStatus)}
          isDisabled={isUpdatingStatus}
        >
          Guardar
        </Button>
        <Button
          {...primaryButtonProps}
          onClick={handleStatusUpdate}
          isLoading={isUpdatingStatus}
          loadingText={isInProgress ? 'Finalizando...' : 'Actualizando...'}
        >
          {getButtonText(currentStatus)}
        </Button>
        <Button
          {...dangerOutlineButtonProps}
          onClick={() => setCancelModalState({ isOpen: true })}
          isDisabled={isUpdatingStatus}
        >
          Cancelar servicio
        </Button>
        <CancelModal
          isOpen={cancelModalState.isOpen}
          onClose={() => setCancelModalState({ isOpen: false })}
          onConfirm={handleCancelService}
          changeObservation={(e) => setCancelObservation(e.target.value)}
        />
      </HStack>
    );
  };

  const renderBody = () => {
    if (!selectedEvent) return null;
    if (currentStatus === 'En curso') {
      return (
        <SchedulerModalForm
          selectedEvent={selectedEvent}
          vehicleService={vehicleService}
          completedEvents={completedEvents}
          setCompletedEvents={setCompletedEvents}
          selectedRecommendedProducts={selectedRecommendedProducts}
          setSelectedRecommendedProducts={setSelectedRecommendedProducts}
          additionalProducts={additionalProducts}
          setAdditionalProducts={setAdditionalProducts}
          observations={mechanicObservations}
          setObservations={setMechanicObservations}
          serviceObservations={serviceObservations}
          setServiceObservations={setServiceObservations}
          productsList={productsList}
          mechanicsList={mechanicsList}
          assistant={assistant}
          setAssistant={setAssistant}
          onMechanicChange={handleMechanicChange}
          customerObservations={customerObservations}
          setCustomerObservations={setCustomerObservations}
          hasAlerts={hasAlerts}
          onOpenAlerts={onAlertsOpen}
          showAssociateLastReception={false}
          productivityTimer={productivityTimer}
          assistantProductivityTimer={assistantProductivityTimer}
          assistantName={
            mechanicsList?.find((m) => Number(m.id) === Number(assistant))
              ?.name || vehicleService?.assistant?.name
          }
          dynamicTestStart={dynamicTestStart}
          dynamicTestEnd={dynamicTestEnd}
          dynamicTestNotApplicable={dynamicTestNotApplicable}
          onStartDynamicTest={handleStartDynamicTest}
          onEndDynamicTest={handleEndDynamicTest}
          onDynamicTestNotApplicableChange={handleDynamicTestNotApplicableChange}
          isSavingDynamicTest={isSavingDynamicTest}
        />
      );
    }
    if (currentStatus === 'Recepcionado') {
      return (
        <SchedulerReceptionModalForm
          readOnly={isUserMechanic}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          vehicleService={vehicleService}
          customerObservations={customerObservations}
          setCustomerObservations={setCustomerObservations}
          serviceObservations={serviceObservations}
          setServiceObservations={setServiceObservations}
          volkswagenCost={volkswagenCost}
          setVolkswagenCost={setVolkswagenCost}
          hasAlerts={hasAlerts}
          onOpenAlerts={onAlertsOpen}
          showAssociateLastReception={showAssociateLastReception}
          onAssociateLastReception={handleAssociateLastReception}
          associatingLastReception={associatingLastReception}
          assistant={assistant}
          setAssistant={setAssistant}
          mechanicsList={mechanicsList}
          onMechanicChange={handleMechanicChange}
        />
      );
    }
    return (
      <SchedulerModalInfo
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
        handleStatusUpdate={handleStatusUpdate}
        customerObservations={customerObservations}
        setCustomerObservations={setCustomerObservations}
        mechanicObservations={mechanicObservations}
        setMechanicObservations={setMechanicObservations}
        handleSaveVehicleService={handleSaveVehicleService}
        handleSaveVehicleData={handleSaveVehicleData}
        statusList={statusList}
        mechanicsList={mechanicsList}
        servicesCatalog={servicesCatalog}
        hasAlerts={hasAlerts}
        onOpenAlerts={onAlertsOpen}
        showAssociateLastReception={showAssociateLastReception}
        onAssociateLastReception={handleAssociateLastReception}
        associatingLastReception={associatingLastReception}
        assistant={assistant}
        setAssistant={setAssistant}
        onMechanicChange={handleMechanicChange}
        vehicleService={vehicleService}
        appointmentList={appointmentList}
        holidayList={holidayList}
        onScheduleOverlayChange={setScheduleOverlayOpen}
        lockEdits={isUserMechanic}
      />
    );
  };

  const footerButtons = renderFooterButtons();

  const canCloseWithoutConfirm =
    (isUserMechanic && currentStatus !== 'En curso') ||
    !currentStatus ||
    FINISHED_STATUSES.includes(currentStatus) ||
    currentStatus === 'Cancelado';

  const requestCloseModal = () => {
    if (isCloseConfirmOpen || isSavingAndClosing) return;
    if (canCloseWithoutConfirm) {
      handleCloseModal();
      return;
    }
    setIsCloseConfirmOpen(true);
  };

  const discardAndCloseModal = () => {
    setIsCloseConfirmOpen(false);
    handleCloseModal();
  };

  const saveAndCloseModal = async () => {
    if (!currentStatus) {
      discardAndCloseModal();
      return;
    }
    setIsSavingAndClosing(true);
    try {
      if (selectedEvent?.appointment?.vehicle) {
        await handleSaveVehicleData(selectedEvent.appointment.vehicle);
      }
      await handleSaveVehicleService(currentStatus, { silent: true });
      setIsCloseConfirmOpen(false);
      handleCloseModal();
    } catch (err) {
      toast({
        title: 'No se pudo guardar',
        description:
          err?.message || 'Revisá los datos e intentá nuevamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingAndClosing(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={requestCloseModal}
        size="full"
        scrollBehavior="inside"
        blockScrollOnMount={false}
        preserveScrollBarGap
        trapFocus={
          !isAlertsOpen &&
          !scheduleOverlayOpen &&
          !isCloseConfirmOpen &&
          !isDeleteConfirmOpen
        }
        closeOnOverlayClick={false}
        closeOnEsc={!isCloseConfirmOpen && !isDeleteConfirmOpen}
      >
        <ModalOverlay
          bg="blackAlpha.500"
          opacity={isAlertsOpen || scheduleOverlayOpen ? 0 : 1}
          pointerEvents={isAlertsOpen || scheduleOverlayOpen ? 'none' : 'auto'}
        />
        <AdminModalContent
          w="100vw"
          maxW="100vw"
          h="100vh"
          maxH="100vh"
          m={0}
          borderRadius={0}
          display="flex"
          flexDirection="column"
          pointerEvents={
            isAlertsOpen || scheduleOverlayOpen ? 'none' : 'auto'
          }
        >
          <Flex
            align="center"
            justify="space-between"
            pl={5}
            pr={14}
            py={4}
            borderBottomWidth="1px"
            borderColor={borderColor}
            flexShrink={0}
          >
            <Box pr={8}>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Detalle del turno
              </Text>
              <Text fontSize="lg" fontWeight="bold" lineHeight="short">
                {selectedEvent?.appointment?.service?.name ||
                  selectedEvent?.appointment?.description ||
                  'Servicio'}
              </Text>
            </Box>
            {currentStatus && (
              <Badge {...getStatusBadgeProps(currentStatus)} flexShrink={0}>
                {currentStatus}
              </Badge>
            )}
          </Flex>
          <ModalCloseButton top={3} right={3} />
          <ModalBody py={4} px={{ base: 4, md: 5 }} flex="1" overflowY="auto">
            {renderBody()}
          </ModalBody>
          {footerButtons && (
            <ModalFooter
              py={3}
              px={5}
              borderTopWidth="1px"
              borderColor={borderColor}
              flexDirection="column"
              gap={2}
              flexShrink={0}
            >
              {footerButtons}
            </ModalFooter>
          )}
        </AdminModalContent>
      </Modal>

      <ServiceAlertsModal
        isOpen={isAlertsOpen}
        onClose={onAlertsClose}
        vehicleId={selectedEvent?.appointment?.vehicle?.id}
        vehicleServiceId={vehicleService?.id}
        onAlertsChanged={() => {
          checkAlerts(selectedEvent?.appointment?.vehicle?.id);
          onAlertsChanged?.();
        }}
      />

      <ConfirmModal
        nested
        isOpen={isCloseConfirmOpen}
        onClose={() => setIsCloseConfirmOpen(false)}
        onConfirm={discardAndCloseModal}
        onSaveAndClose={saveAndCloseModal}
        isSaveAndCloseLoading={isSavingAndClosing}
        header="¿Cerrar sin guardar?"
        message="Si no guardás, se van a perder los cambios no guardados."
        cancelLabel={null}
        confirmLabel="Cerrar sin guardar"
        confirmVariant="danger"
        saveAndCloseLabel="Guardar y cerrar"
      />

      <ConfirmModal
        nested
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteService}
        header="Eliminar servicio"
        message="Esta acción eliminará definitivamente el servicio y su cita. El stock consumido ya fue restaurado al cancelar."
        confirmLabel="Eliminar definitivamente"
        confirmVariant="danger"
        isLoading={isDeletingService}
      />

      <Modal
        isOpen={stockAlertState.isOpen}
        onClose={() => setStockAlertState({ isOpen: false, shortages: [] })}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <AdminModalContent maxW="480px">
          <ModalHeader fontSize="md">Falta de stock</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.700" mb={3}>
              No se puede confirmar el servicio. Los siguientes productos no tienen
              stock suficiente:
            </Text>
            <VStack align="stretch" spacing={2}>
              {stockAlertState.shortages.map((item) => (
                <Box
                  key={item.productId}
                  p={3}
                  borderRadius="md"
                  bg="orange.50"
                  borderWidth="1px"
                  borderColor="orange.200"
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    {item.name}
                  </Text>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Necesario: {item.requested} · Disponible: {item.available}
                  </Text>
                </Box>
              ))}
            </VStack>
            <Text fontSize="xs" color="gray.500" mt={3}>
              Registre los lotes faltantes en Inventario antes de confirmar.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              {...primaryButtonProps}
              onClick={() => setStockAlertState({ isOpen: false, shortages: [] })}
            >
              Entendido
            </Button>
          </ModalFooter>
        </AdminModalContent>
      </Modal>
    </>
  );
};

export default SchedulerModal;
